/**
 * A real tag (`<p>`, `<br>`, `<a href="mailto:…">`).
 * An address written as `<name@domain>` is not a tag. Treating it as one
 * made Hub show and send replies as a single line.
 */
const REAL_HTML_TAG = /<\/?[a-z][a-z0-9]*(?=\s|\/?>)[^>]*>/i;

export function looksLikeHtml(value: string): boolean {
  return REAL_HTML_TAG.test(value);
}

/** Strip tags for plain-text send / discard checks. Keeps `<name@domain>`. */
export function stripHtml(html: string): string {
  if (!html) return "";
  return html
    .replace(/<style[\s\S]*?<\/style>/gi, "")
    .replace(/<script[\s\S]*?<\/script>/gi, "")
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/p>/gi, "\n\n")
    .replace(/<\/div>/gi, "\n")
    .replace(/<\/li>/gi, "\n")
    .replace(/<li[^>]*>/gi, "• ")
    .replace(/<\/h[1-6]>/gi, "\n\n")
    .replace(/<\/blockquote>/gi, "\n")
    .replace(new RegExp(REAL_HTML_TAG.source, "gi"), "")
    .replace(/&nbsp;/g, " ")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&amp;/g, "&")
    .replace(/&quot;/g, '"')
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}

/**
 * Newlines typed or pasted into the composer are real line breaks on screen
 * (`white-space: pre-wrap`), but email HTML collapses them. Turn those into
 * `<br>` and leave indentation between tags alone so a normal HTML message
 * does not grow blank lines.
 */
export function htmlTextNewlinesToBreaks(html: string): string {
  if (!html || !/[\r\n]/.test(html)) return html;
  let skip = 0;
  return html
    .split(/(<[^>]*>)/g)
    .map((part) => {
      if (part.startsWith("<")) {
        if (/^<(style|script|pre)\b/i.test(part)) skip += 1;
        else if (/^<\/(style|script|pre)\b/i.test(part)) skip = Math.max(0, skip - 1);
        return part;
      }
      if (skip || !/[\r\n]/.test(part) || !part.trim()) return part;
      return part.replace(/[ \t]*\r\n[ \t]*|[ \t]*\n[ \t]*/g, (nl, offset, str) => {
        const before = str.slice(0, offset);
        const after = str.slice(offset + nl.length).replace(/[\r\n]/g, "");
        return /\S/.test(before) && /\S/.test(after) ? "<br>" : nl;
      });
    })
    .join("");
}

/**
 * True when clipboard HTML already has a break for each plain-text line.
 * A single wrapping tag does not count: WebKit then keeps the newlines only
 * as text, and Gmail shows the paste as one line.
 */
export function clipboardHtmlKeepsLineBreaks(html: string, plain: string): boolean {
  const lines = plain.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  if (lines.length <= 1) return true;
  const breaks = html.match(/<br\b|<\/(?:p|div|li|tr|blockquote|h[1-6]|pre)>/gi);
  return (breaks?.length || 0) >= lines.length - 1;
}

export function escapeHtml(text: string): string {
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

/**
 * Bare http(s) URLs in plain text. Trailing sentence punctuation is stripped
 * after the match so `https://zoom.us/j/1.` keeps the period outside the link,
 * while path segments like `...pwd=….1` stay inside.
 */
const BARE_URL = /\bhttps?:\/\/[^\s<>"']+/gi;

/** Gmail-style dial-in numbers (`+1 646 558 8656`); skips meeting IDs without `+`. */
const BARE_PHONE = /\+\d(?:[\s().-]*\d){6,}/g;

const TRAILING_URL_PUNCT = /[),.;:!?]+$/;

function trimUrlMatch(raw: string): string {
  let href = raw;
  while (TRAILING_URL_PUNCT.test(href)) {
    // Keep a trailing `.digit` / path fragment (Zoom pwd=….1).
    if (/\.\d$/.test(href)) break;
    // Keep balanced closing parens that belong to the URL.
    if (href.endsWith(")")) {
      const open = (href.match(/\(/g) || []).length;
      const close = (href.match(/\)/g) || []).length;
      if (close <= open) break;
    }
    href = href.slice(0, -1);
  }
  return href;
}

type LinkHit = { start: number; end: number; href: string; label: string };

function collectLinkHits(text: string): LinkHit[] {
  const hits: LinkHit[] = [];
  for (const match of text.matchAll(BARE_URL)) {
    const raw = match[0];
    const start = match.index ?? 0;
    const href = trimUrlMatch(raw);
    if (!href) continue;
    hits.push({ start, end: start + href.length, href, label: href });
  }
  for (const match of text.matchAll(BARE_PHONE)) {
    const label = match[0];
    const start = match.index ?? 0;
    const end = start + label.length;
    if (hits.some((hit) => start < hit.end && end > hit.start)) continue;
    const digits = label.replace(/\D/g, "");
    if (digits.length < 8 || digits.length > 16) continue;
    hits.push({ start, end, href: `tel:${digits}`, label });
  }
  hits.sort((a, b) => a.start - b.start || b.end - a.end);
  const merged: LinkHit[] = [];
  for (const hit of hits) {
    const prev = merged[merged.length - 1];
    if (prev && hit.start < prev.end) continue;
    merged.push(hit);
  }
  return merged;
}

/** Escape an attribute value. Leaves existing `&amp;` / `&lt;` entities alone. */
function escapeHrefAttr(value: string): string {
  if (/&(?:amp|lt|gt|quot|#\d+|#x[\da-f]+);/i.test(value)) {
    return value.replace(/"/g, "&quot;");
  }
  return escapeHtml(value);
}

/**
 * Escape plain text and wrap bare URLs / +phone numbers in anchors
 * (same idea as Gmail's reader autolink).
 */
export function escapeAndLinkify(text: string): string {
  if (!text) return "";
  const hits = collectLinkHits(text);
  if (!hits.length) return escapeHtml(text);
  let out = "";
  let cursor = 0;
  for (const hit of hits) {
    if (hit.start > cursor) out += escapeHtml(text.slice(cursor, hit.start));
    out += `<a href="${escapeHtml(hit.href)}">${escapeHtml(hit.label)}</a>`;
    cursor = hit.end;
  }
  if (cursor < text.length) out += escapeHtml(text.slice(cursor));
  return out;
}

/**
 * Wrap bare URLs / +phones in already-safe HTML text (no re-escape of entities).
 */
function linkifyHtmlText(text: string): string {
  const hits = collectLinkHits(text);
  if (!hits.length) return text;
  let out = "";
  let cursor = 0;
  for (const hit of hits) {
    out += text.slice(cursor, hit.start);
    out += `<a href="${escapeHrefAttr(hit.href)}">${hit.label}</a>`;
    cursor = hit.end;
  }
  return out + text.slice(cursor);
}

const LINKIFY_SKIP_TAGS = new Set(["a", "script", "style", "textarea", "button", "code", "svg"]);

/**
 * Linkify bare URLs / +phones in HTML text nodes only. Leaves existing
 * &lt;a&gt; tags alone so provider and compose links are not double-wrapped.
 * Regex split (no DOM) so Node selftests stay aligned with the reader.
 */
export function linkifyHtml(html: string): string {
  if (!html || !/(?:https?:\/\/|\+\d)/i.test(html)) return html;
  const skipStack: string[] = [];
  return html
    .split(/(<[^>]+>)/g)
    .map((part) => {
      if (!part.startsWith("<")) {
        return skipStack.length ? part : linkifyHtmlText(part);
      }
      const close = /^<\/\s*([a-z][a-z0-9]*)\b/i.exec(part);
      if (close) {
        const name = close[1].toLowerCase();
        const idx = skipStack.lastIndexOf(name);
        if (idx >= 0) skipStack.splice(idx, 1);
        return part;
      }
      const open = /^<\s*([a-z][a-z0-9]*)\b/i.exec(part);
      if (open && !/\/\s*>$/.test(part)) {
        const name = open[1].toLowerCase();
        if (LINKIFY_SKIP_TAGS.has(name)) skipStack.push(name);
      }
      return part;
    })
    .join("");
}

/** Convert plain compose text (including reply quotes) into simple HTML. */
export function plainToHtml(text: string): string {
  if (!text.trim()) {
    // Keep a blank editable line when the draft is only whitespace / newlines.
    return text ? "<p><br></p>" : "";
  }
  const lines = text.replace(/\r\n/g, "\n").split("\n");
  const blocks: string[] = [];
  let para: string[] = [];
  let quote: string[] = [];

  function flushPara() {
    if (!para.length) return;
    const inner = para.map((line) => escapeAndLinkify(line)).join("<br>");
    blocks.push(`<p>${inner}</p>`);
    para = [];
  }

  function flushQuote() {
    if (!quote.length) return;
    const inner = quote.map((line) => escapeAndLinkify(line)).join("<br>");
    blocks.push(`<blockquote>${inner}</blockquote>`);
    quote = [];
  }

  for (const line of lines) {
    if (line.startsWith("> ")) {
      flushPara();
      quote.push(line.slice(2));
      continue;
    }
    if (line === ">" ) {
      flushPara();
      quote.push("");
      continue;
    }
    flushQuote();
    if (line === "") {
      flushPara();
      // Preserve blank lines (including leading) so Reply has room to type above the quote.
      blocks.push("<p><br></p>");
      continue;
    }
    para.push(line);
  }
  flushQuote();
  flushPara();
  return blocks.join("") || `<p>${escapeAndLinkify(text)}</p>`;
}

export function isBlankHtml(html: string): boolean {
  return !stripHtml(html).trim();
}
